/**
 * ConclusionCategoryStatusTable — the golden "Category statuses,
 * evidence-justified" table (golden-convergence GC-W4).
 *
 * The audit's HIGH gap #4 was that NO surface rendered the golden conclusion's
 * 3-column CATEGORY / STATUS (coloured dot) / JUSTIFYING EVIDENCE table. The
 * data already exists — `report.categories` (RagCategory[]) carries `label`,
 * `status` (green/amber/red), and `explanation` (the per-category justifying
 * evidence the score was derived from). This primitive renders exactly that:
 * a clean, evidence-true status table so the conclusion's verdict is backed by
 * a scannable, category-level justification (not just prose).
 *
 * Theme-aware (roleColor — follows the active preset) + null-safe (collapses to
 * null when the report carries no scored categories, e.g. a legacy/unscored
 * report). The status DOT is the primary signal; the band WORD
 * (Strong/Watch/Risk) supports it and reads off the same RagStatus.
 */
import React from 'react';
import type { SiteRagReport, RagCategory, RagStatus } from '../../types/siteRag';
import { useEffectiveReportTheme } from '../../lib/useEffectiveReportTheme';
import { roleColor, type ReportColorRole } from '../../lib/reportRoleColors';
import { evaluateTopographySlopeProvenance } from '../../lib/reportRendering';

interface Props {
  report: SiteRagReport;
}

const STATUS_ROLE: Record<RagStatus, ReportColorRole> = {
  green: 'positive',
  amber: 'warning',
  red: 'negative',
};

function hasRedMetricOrHighWaterStress(c: RagCategory): boolean {
  return (
    (c.metrics ?? []).some(
      (m) => m.status === 'red' || (m.id === 'water_stress' && /high|extreme/i.test(String(m.value ?? ''))),
    ) || /Water Stress Index:\s*(?:High|Extreme)\s*\(red\)/i.test(c.explanation ?? '')
  );
}

function hasMissingOrPlanningOnly(c: RagCategory): boolean {
  return (
    (c.metrics ?? []).some((m) => m.missing) ||
    (c.metrics ?? []).some((m) => /planning-default\s+elcc/i.test(`${m.label ?? ''} ${m.value ?? ''}`)) ||
    /evidence gap\s*[—–-]\s*excluded from scoring|planning-default\s+elcc/i.test(c.explanation ?? '')
  );
}

function effectiveStatus(c: RagCategory): RagStatus {
  if (c.status === 'green' && (hasRedMetricOrHighWaterStress(c) || hasMissingOrPlanningOnly(c))) {
    return 'amber';
  }
  return c.status;
}

/** Supporting band word for the status dot (matches the radar's ladder). */
function statusBand(c: RagCategory): string {
  switch (c.status) {
    case 'green': {
      if (c.id === 'cooling' && hasRedMetricOrHighWaterStress(c)) {
        return 'Conditional (climate favorable; supply/design unverified)';
      }
      if (c.id === 'regulatory' && hasMissingOrPlanningOnly(c)) {
        return 'Conditional (state proxy; local zoning unverified)';
      }
      return hasMissingOrPlanningOnly(c) || hasRedMetricOrHighWaterStress(c)
        ? 'Conditional (partial basis)'
        : 'Strong';
    }
    case 'amber': return 'Watch';
    case 'red': return 'Risk';
    default: return '—';
  }
}

/** Display-layer guard: round raw floats that leak into evidence strings
 *  (e.g. "1170.1054573547272 acres", "10.245000000000001 ms") to a sane
 *  precision — ≥100 → integer, else 1 dp. Defensive; the upstream evidence
 *  builders should also round, but this catches any that don't. */
export function sanitizeEvidenceNumbers(s: string): string {
  return s.replace(/-?\d+\.\d{3,}/g, (m) => {
    const n = Number(m);
    if (!Number.isFinite(n)) return m;
    const rounded = Math.abs(n) >= 100 ? Math.round(n) : Math.round(n * 10) / 10;
    return String(rounded);
  });
}

function hasSinglePointSlope(report: SiteRagReport): boolean {
  return evaluateTopographySlopeProvenance(report).singlePointZeroSlope;
}

function hasUnverifiedZeroSlopeProxy(report: SiteRagReport): boolean {
  return evaluateTopographySlopeProvenance(report).unverifiedZeroSlopeNoProvenance;
}

function hasUnverified3DepSlopeProxy(report: SiteRagReport): boolean {
  return evaluateTopographySlopeProvenance(report).unverified3DepSlopeProxy;
}

/** First non-empty sentence/line of the category explanation (kept concise for
 *  the evidence column); falls back to a status-derived line when absent. */
function justifyingEvidence(c: RagCategory, report: SiteRagReport): string {
  let raw = c.explanation?.trim();
  if (raw) {
    if (c.id === 'site' && hasSinglePointSlope(report)) {
      raw = raw.replace(
        /Topography\s*\/\s*Slope:\s*0(?:\.0+)?\s*%\s*slope\s*\(green\)/i,
        'Topography / Slope: slope unverified (single-point elevation sample)',
      );
    } else if (c.id === 'site' && hasUnverifiedZeroSlopeProxy(report)) {
      raw = raw.replace(
        /Topography\s*\/\s*Slope:\s*0(?:\.0+)?\s*%\s*slope\s*\(green\)/i,
        'Topography / Slope: slope unverified (0% elevation-relief proxy without bound survey provenance – excluded from scoring)',
      );
    } else if (c.id === 'site' && hasUnverified3DepSlopeProxy(report)) {
      raw = raw.replace(
        /Topography\s*\/\s*Slope:\s*(\d+(?:\.\d+)?)\s*%\s*slope\s*\(green\)/i,
        'Topography / Slope: $1% regional relief proxy (amber — local slope derivation unverified; site topographic survey required)',
      );
    }
    return sanitizeEvidenceNumbers(raw);
  }
  switch (c.status) {
    case 'green': return 'Supported by the provider/SED evidence for this category.';
    case 'amber': return 'Mixed or incomplete evidence — verify before final approval.';
    case 'red': return 'Material gap or risk flagged in this category.';
    default: return '—';
  }
}

export function ConclusionCategoryStatusTable({ report }: Props): React.ReactElement | null {
  const theme = useEffectiveReportTheme();
  const cats = report.categories ?? [];
  if (cats.length === 0) return null;

  const headerStyle: React.CSSProperties = {
    fontFamily: 'var(--report-body-font, inherit)',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: roleColor(theme, 'muted'),
    textAlign: 'left',
    padding: '0 12px 7px 0',
    borderBottom: `1.5px solid ${roleColor(theme, 'ink')}`,
    whiteSpace: 'nowrap',
  };

  return (
    <div
      className="mb-6"
      data-testid="conclusion-category-status-table"
      data-paged-split-after="true"
      data-paged-split-kind="conclusion-status"
      // The table is a row stream, not one page-sized exhibit. In the measured
      // deck the shared row-group contract turns this carrier into ordinary
      // block flow while each semantic <tr> remains atomic below.
      data-layout-group="row-group"
      data-paged-table-group="conclusion-status"
    >
      <div
        className="mb-3"
        data-paged-table-label="conclusion-status"
        style={{
          fontFamily: 'var(--report-body-font, inherit)',
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: roleColor(theme, 'ink'),
          breakAfter: 'avoid',
          pageBreakAfter: 'avoid',
        }}
      >
        Category statuses, evidence-justified
      </div>
      <table
        data-table-splittable="true"
        data-paged-table-kind="conclusion-status"
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          // Opt out of the deck-wide short-table atomic rule. The repeated
          // header + row keeps below provide the safe fragmentation contract.
          breakInside: 'auto',
          pageBreakInside: 'auto',
          breakBefore: 'avoid',
          pageBreakBefore: 'avoid',
        }}
      >
        <colgroup>
          <col style={{ width: '21%' }} />
          <col style={{ width: '23%' }} />
          <col style={{ width: '56%' }} />
        </colgroup>
        <thead data-paged-table-header="conclusion-status">
          <tr>
            <th style={headerStyle}>Category</th>
            <th style={headerStyle}>Status</th>
            <th style={{ ...headerStyle, paddingRight: 0 }}>Justifying evidence</th>
          </tr>
        </thead>
        <tbody data-paged-table-body="conclusion-status">
          {cats.map((c, rowIndex) => {
            const effStatus = effectiveStatus(c);
            const dot = roleColor(theme, STATUS_ROLE[effStatus] ?? 'muted');
            // CSS orphans/widows do not apply to table rows. Keep exactly the
            // first two rows together (row 0 break-after) and exactly the last
            // two together (last row break-before). Do not stamp row 1 or the
            // penultimate row: doing so chains three rows and recreates the
            // large blank tail this table is meant to prevent.
            const rowKeepStyle: React.CSSProperties = {
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
              ...(rowIndex === 0
                ? { breakAfter: 'avoid', pageBreakAfter: 'avoid' }
                : {}),
              ...(rowIndex === cats.length - 1
                ? { breakBefore: 'avoid', pageBreakBefore: 'avoid' }
                : {}),
            };
            return (
              <tr
                key={c.id}
                data-testid={`conclusion-status-row-${c.id}`}
                data-paged-table-row="conclusion-status"
                data-paged-row-key={c.id}
                style={rowKeepStyle}
              >
                <td
                  style={{
                    padding: '9px 12px 9px 0',
                    verticalAlign: 'top',
                    fontWeight: 600,
                    fontSize: 13,
                    color: roleColor(theme, 'ink'),
                    borderBottom: `1px solid ${roleColor(theme, 'rule')}`,
                  }}
                >
                  {c.label}
                </td>
                <td
                  style={{
                    padding: '9px 12px 9px 0',
                    verticalAlign: 'top',
                    borderBottom: `1px solid ${roleColor(theme, 'rule')}`,
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                    <span
                      aria-hidden="true"
                      style={{ width: 8, height: 8, marginTop: 4, borderRadius: '9999px', background: dot, flex: '0 0 8px' }}
                    />
                    <span style={{ fontSize: 12, lineHeight: 1.35, fontWeight: 600, color: dot }} data-status={effStatus}>
                      {statusBand(c)}
                    </span>
                  </span>
                </td>
                <td
                  style={{
                    padding: '9px 0',
                    verticalAlign: 'top',
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: roleColor(theme, 'body'),
                    borderBottom: `1px solid ${roleColor(theme, 'rule')}`,
                  }}
                >
                  {justifyingEvidence(c, report)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
